import type { ApiErrorBody, AuthResponseDto } from '@lagerfeuer/shared';
import { useAuthStore } from '../store/auth';

/** Fehler im einheitlichen API-Format (docs/API.md „Fehlerformat"). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
  }
  get code(): string {
    return this.body.error;
  }
}

/** Menschlich lesbare Fehlermeldung für beliebige Fehler. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VALIDATION_FAILED' && e.body.details) {
      const first = Object.values(e.body.details).flat()[0];
      if (first) return first;
    }
    return e.message || e.code;
  }
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler';
}

let refreshing: Promise<boolean> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
/** Fehlversuche in Folge (vorübergehende Fehler wie 502/429/Netz) für den Backoff. */
let refreshFailures = 0;
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 5 * 60_000;

/** Ergebnis des letzten Refresh: ok, Sitzung ungültig (401/403) oder vorübergehender Fehler (5xx/429/Netz). */
export type RefreshOutcome = 'ok' | 'auth' | 'transient';
let lastOutcome: RefreshOutcome | null = null;
export function lastRefreshOutcome(): RefreshOutcome | null {
  return lastOutcome;
}

/** Sitzung übernehmen und die Erneuerung kurz vor Ablauf des Access-Tokens planen. */
export function applyAuthResponse(data: AuthResponseDto): void {
  useAuthStore.getState().setSession(data.accessToken, data.user);
  scheduleRefresh(data.expiresIn);
}

/** Plant eine proaktive Erneuerung ~60 s vor Ablauf (mindestens nach 10 s). */
export function scheduleRefresh(expiresInSeconds: number): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) return;
  const ms = Math.max(10, expiresInSeconds - 60) * 1000;
  refreshTimer = setTimeout(() => {
    void refreshAccessToken();
  }, ms);
}

export function cancelScheduledRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}

/** Wartezeit vor dem n-ten erneuten Versuch (exponentiell, gedeckelt, mit etwas Streuung). */
export function refreshRetryDelay(failures: number): number {
  const base = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.max(0, failures - 1));
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

/** Vorübergehender Fehler: Sitzung behalten, später erneut versuchen (nur mit aktiver Sitzung). */
function scheduleRetry(): void {
  refreshFailures++;
  if (!useAuthStore.getState().accessToken) return;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    void refreshAccessToken();
  }, refreshRetryDelay(refreshFailures));
}

/**
 * Access-Token über das httpOnly-Refresh-Cookie erneuern. Gleichzeitige Aufrufe teilen sich eine Anfrage
 * (Refresh-Tokens rotieren – ein doppelter Refresh würde die Sitzung widerrufen, D13).
 * Nur 401/403 beenden die Sitzung; 5xx/429/Netzfehler (z. B. 502 während eines Deploys) liefern `false`
 * und planen einen erneuten Versuch mit Backoff.
 */
export function refreshAccessToken(): Promise<boolean> {
  refreshing ??= withRefreshLock(() => fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' }))
    .then(async (res) => {
      if (res.status === 401 || res.status === 403) {
        lastOutcome = 'auth';
        refreshFailures = 0;
        cancelScheduledRefresh();
        useAuthStore.getState().clear();
        return false;
      }
      if (!res.ok) {
        lastOutcome = 'transient';
        scheduleRetry();
        return false;
      }
      lastOutcome = 'ok';
      refreshFailures = 0;
      applyAuthResponse((await res.json()) as AuthResponseDto);
      return true;
    })
    .catch(() => {
      lastOutcome = 'transient';
      scheduleRetry();
      return false;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/** So lange nach dem eigenen Passwortwechsel gilt ein `INVALID_SESSION {reason:'password_changed'}` als „eigener". */
export const OWN_PASSWORD_CHANGE_GRACE_MS = 15_000;
let ownPasswordChange: { result: Promise<boolean>; until: number } | null = null;

/**
 * Eigenen Passwortwechsel vormerken. Der Server rotiert dabei das Refresh-Cookie dieser Sitzung, macht aber alle
 * Access-Tokens ungültig und schickt `INVALID_SESSION {reason:'password_changed'}` an alle Sockets des Nutzers –
 * auch an dieses Gerät, oft noch vor der HTTP-Antwort. Das Gateway wartet dann auf das Ergebnis und meldet sich mit
 * dem neuen Cookie neu an, statt dieses Gerät abzumelden.
 */
export function trackOwnPasswordChange<T>(request: Promise<T>): Promise<T> {
  const entry = { result: request.then(() => true, () => false), until: Number.POSITIVE_INFINITY };
  ownPasswordChange = entry;
  void entry.result.then(() => {
    entry.until = Date.now() + OWN_PASSWORD_CHANGE_GRACE_MS;
  });
  return request;
}

/** Ergebnis eines laufenden bzw. eben beendeten eigenen Passwortwechsels (`true` = erfolgreich), sonst `null`. */
export function ownPasswordChangeResult(): Promise<boolean> | null {
  const e = ownPasswordChange;
  return e && Date.now() <= e.until ? e.result : null;
}

/**
 * Tab-übergreifend serialisieren (Web Locks): Öffnen mehrere Tabs gleichzeitig, würde ein paralleler Refresh mit
 * demselben Cookie als Wiederverwendung erkannt und die Sitzung widerrufen. Hintereinander rotiert jeder Tab sauber.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  return locks?.request ? (locks.request('lf-auth-refresh', fn) as Promise<T>) : fn();
}

export type ApiInit = RequestInit & { json?: unknown; query?: Record<string, string | number | boolean | null | undefined | string[]> };

/**
 * Pfadsegmente aus Routen/Query (`?thread=../users/@me`) dürfen den API-Pfad nicht verlassen oder verbiegen:
 * Punkt-Segmente, Backslashes, `?`/`#` im Pfad und Steuerzeichen werden abgelehnt.
 */
export function assertSafePath(path: string): void {
  // eslint-disable-next-line no-control-regex
  if (/(^|\/)\.{1,2}(\/|$)|\\|[?#]|[\u0000-\u001f]|%2e|%2f|%5c/i.test(path)) {
    throw new ApiError(400, { statusCode: 400, error: 'VALIDATION_FAILED', message: 'Ungültiger Pfad' });
  }
}

export function buildUrl(path: string, query?: ApiInit['query']): string {
  assertSafePath(path);
  let url = path.startsWith('/api') ? path : `/api${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) for (const x of v) qs.append(k, x);
      else qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  return url;
}

/** Auth-Routen erneuern nie selbst (sonst Schleife bzw. falsches Verhalten bei INVALID_CREDENTIALS). */
const isAuthPath = (path: string) => /^(\/api)?\/auth\/(login|register|refresh|logout)\b/.test(path);

/** Soll nach einem 401 ein Refresh versucht werden? */
export function shouldRefresh(status: number, code: string | undefined, path: string): boolean {
  if (status !== 401 || isAuthPath(path)) return false;
  return code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED' || code === undefined;
}

/** fetch-Wrapper: setzt Bearer-Token, JSON, erneuert einmal bei 401 TOKEN_EXPIRED. */
export async function api<T>(path: string, init: ApiInit = {}, retry = true): Promise<T> {
  const { json, query, ...rest } = init;
  const headers = new Headers(rest.headers);
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set('Authorization', `Bearer ${token}`);
  let body = rest.body;
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  }
  const res = await fetch(buildUrl(path, query), { ...rest, headers, body, credentials: 'include' });
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as ApiErrorBody | null) ?? { statusCode: res.status, error: 'INTERNAL', message: res.statusText };
    if (retry && shouldRefresh(res.status, err.error, path) && (await refreshAccessToken())) return api<T>(path, init, false);
    throw new ApiError(res.status, err);
  }
  return data as T;
}

export interface UploadOptions {
  method?: 'POST' | 'PUT' | 'PATCH';
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

/**
 * Multipart-Upload per XHR (fetch kennt keinen Upload-Fortschritt). Erneuert einmal bei abgelaufenem Token.
 */
export function apiUpload<T>(path: string, form: FormData, opts: UploadOptions = {}, retry = true): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (opts.signal?.aborted) {
      reject(new DOMException('Abgebrochen', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method ?? 'POST', buildUrl(path));
    xhr.withCredentials = true;
    const token = useAuthStore.getState().accessToken;
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded, e.lengthComputable ? e.total : 0);
    const onAbort = () => xhr.abort();
    const cleanup = () => opts.signal?.removeEventListener('abort', onAbort);
    opts.signal?.addEventListener('abort', onAbort, { once: true });
    xhr.onerror = () => {
      cleanup();
      reject(new ApiError(0, { statusCode: 0, error: 'NETWORK', message: 'Netzwerkfehler beim Hochladen' }));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('Abgebrochen', 'AbortError'));
    };
    xhr.onload = () => {
      cleanup();
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve((xhr.status === 204 ? undefined : data) as T);
        return;
      }
      const err = (data as ApiErrorBody | null) ?? { statusCode: xhr.status, error: 'INTERNAL', message: xhr.statusText };
      if (retry && shouldRefresh(xhr.status, err.error, path)) {
        refreshAccessToken()
          .then((ok) => (ok ? apiUpload<T>(path, form, opts, false).then(resolve, reject) : reject(new ApiError(xhr.status, err))))
          .catch(reject);
        return;
      }
      reject(new ApiError(xhr.status, err));
    };
    xhr.send(form);
  });
}

/** Einzelnes Bild/Datei-Feld `file` hochladen (Avatar, Icon, Emoji …), weitere Felder optional. */
export function uploadFile<T>(path: string, file: Blob, fields: Record<string, string> = {}, opts: UploadOptions = {}): Promise<T> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append('file', file);
  return apiUpload<T>(path, form, { method: 'PUT', ...opts });
}
